import { Helmet } from 'react-helmet'
import { useLocation } from '@reach/router'
import { graphql, useStaticQuery } from 'gatsby'

import { CloudinaryImage } from '@cloudinary/url-gen'

import { useTranslation } from '@/utils/useTranslation'
import { defaultLocale, getLocale, localizedPath, useLocale } from '@plugins/bw-i18next'

export const fragment = graphql`
  fragment SEO on ContentfulSeo {
    path
    keywords
    title
    description {
      description
    }
    ogImage {
      ...Asset
    }
    canonical {
      path
      seo {
        help__article {
          slug
        }
      }
    }
    noIndex
    noFollow
    node_locale
  }
`
export interface Props {
  path?: string
  title?: string
  description?: string | Record<string, string>
  keywords?: string[]
  canonical?: Props
  seo?: Record<string, Record<string, string>>
  noIndex?: boolean
  noFollow?: boolean
  ogImage?: Queries.ContentfulAsset
  otherLocales?: string[]
  breadcrumb?: { name: string; path: string }[]
  darkMode?: boolean
}

export default function Meta(props: Props) {
  const { canonical, noIndex, noFollow, otherLocales, breadcrumb, darkMode } = props
  let { title, description, keywords, ogImage } = props

  const { pathname } = useLocation()
  const { t } = useTranslation()
  if (typeof description === 'object') description = description?.description

  const {
    allContentfulLandingPage: { nodes: landingNodes },
    allContentfulSeo: { nodes: pathNodes },
    allContentfulLocales,
  } = useStaticQuery(graphql`
    query AllContentfulSeoAndLocales {
      allContentfulLandingPage(filter: { slug: { eq: "index" } }) {
        nodes {
          seo {
            ...SEO
          }
        }
      }
      allContentfulSeo(filter: { path: { ne: null } }) {
        nodes {
          ...SEO
        }
      }
      allContentfulLocales {
        nodes {
          locales {
            slug
          }
        }
      }
    }
  `)
  const { locales } = allContentfulLocales.nodes[0]

  const { currentLocale, currentContentfulCode, region } = useLocale()

  const pathSeo = pathNodes.find((n) => n.path === pathname && n.node_locale === currentContentfulCode)
  if (pathSeo) {
    if (!title) title = pathSeo.title
    if (!description) description = pathSeo.description?.description
    if (!keywords) keywords = pathSeo.keywords
    if (!ogImage) ogImage = pathSeo.ogImage
  }

  const homeSeo = landingNodes.find((n) => n.seo.node_locale === currentContentfulCode)?.seo
  if (homeSeo) {
    if (!title) title = homeSeo.title
    if (!description) description = homeSeo.description?.description
    if (!keywords?.length) keywords = homeSeo.keywords
    if (!ogImage) ogImage = homeSeo.ogImage
  }

  title += ` | ${t('Bitwarden')}`
  if (pathname.includes('/blog/') && pathname !== '/blog/') {
    title += ` ${t('Blog')}`
  } else if (pathname.includes('/careers/') && pathname !== '/careers/') title += ` ${t('Careers')}`
  else if (pathname.includes('/help/') && pathname !== '/help/') title += ` ${t('Help Center')}`
  else if (pathname.includes('/resources/') && pathname !== '/resources/') {
    title += ` ${t('Resources')}`
  } else if (pathname.includes('/events/') && pathname !== '/events/') {
    title += ` ${t('Events')}`
  }

  let canonicalPath = canonical?.path
  if (canonicalPath?.startsWith('/')) canonicalPath = `${process.env.GATSBY_SITE_HOST}${canonicalPath}`
  else if (canonical?.seo?.help__article)
    canonicalPath = `${process.env.GATSBY_SITE_HOST}/help/${canonical.seo.help__article?.slug}/`

  // use this to lazy migrate
  const path = ogImage?.file?.url?.replace('//images.ctfassets.net/', 'ctf/')
  const cldImg = new CloudinaryImage(path, {
    cloudName: 'bw-com',
  })

  const ogUrl = canonicalPath || `${process.env.GATSBY_SITE_HOST}${pathname}`
  return (
    <Helmet>
      <html lang={currentLocale} dir="ltr" className={darkMode && 'dark'} />
      <title>{title}</title>
      <meta name="description" content={description as string} />
      <meta name="keywords" content={keywords ? keywords.join(', ') : ''} />

      {noIndex && <meta name="robots" content="noindex" />}
      {noFollow && <meta name="robots" content="nofollow" />}

      <link rel="canonical" href={canonicalPath || `${process.env.GATSBY_SITE_HOST}${pathname}`} />

      <meta property="og:site_name" content="Bitwarden" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description as string} />
      <meta property="og:image" content={cldImg.toURL()} />
      <meta property="og:url" content={ogUrl} />
      <meta property="og:type" content={pathname.startsWith('/blog/') ? 'article' : 'website'} />
      {locales.map((locale) => (
        <meta
          key={locale.slug}
          property={`og:locale${locale.slug !== currentLocale ? ':alternate' : ''}`}
          content={locale.slug.replace(`-`, `_`)}
        />
      ))}

      <meta property="twitter:site" content="@bitwarden" />
      <meta property="twitter:domain" content="bitwarden.com" />
      <meta property="twitter:url" content={ogUrl} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description as string} />
      <meta name="twitter:image" content={cldImg.toURL()} />

      <meta name="language" content={currentLocale} />
      <meta name="country" content={region} />

      <link
        rel="alternate"
        hrefLang="x-default"
        href={`${process.env.GATSBY_SITE_HOST}${localizedPath(pathname, defaultLocale)}`}
      />
      {otherLocales?.length > 0
        ? otherLocales.map((other) => (
            <link
              rel="alternate"
              key={other}
              hrefLang={getLocale(other)}
              href={`${process.env.GATSBY_SITE_HOST}${other}`}
            />
          ))
        : locales.map((locale) => (
            <link
              rel="alternate"
              key={locale.slug}
              hrefLang={locale.slug}
              href={`${process.env.GATSBY_SITE_HOST}${localizedPath(pathname, locale.slug)}`}
            />
          ))}
      {otherLocales?.length > 0
        ? otherLocales.map((other) => (
            <link
              rel="alternate"
              key={other}
              hrefLang={getLocale(other).substring(0, 2)}
              href={`${process.env.GATSBY_SITE_HOST}${other}`}
            />
          ))
        : locales.map((locale) => (
            <link
              rel="alternate"
              key={locale}
              hrefLang={locale.slug.substring(0, 2)}
              href={`${process.env.GATSBY_SITE_HOST}${localizedPath(pathname, locale.slug)}`}
            />
          ))}
      {breadcrumb?.length && (
        <script type="application/ld+json">
          {`{
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type" : "ListItem",
                  "position": "1",
                  "name": "${breadcrumb[0].name}",
                  "item": "https://bitwarden.com${breadcrumb[0].path}"
                },
                {
                  "@type": "ListItem",
                  "position": "2",
                  "name": "${breadcrumb[1].name}"
                }
              ]
            }`}
        </script>
      )}
      {pathname === '/' && (
        <script type="application/ld+json">
          {`{
            "@context": "https://schema.org",
            "@type": "Organization",
            "url": "https://bitwarden.com",
            "image": "${cldImg.toURL()}",
            "logo": "https://res.cloudinary.com/bw-com/image/upload/f_auto/v1/ctf/7rncvj1f8mw7/4979BKK2E1UAuehOSlBQIF/7a5a463a3c1c4a3e1931da9feaa737ca/bitwarden-og.png?_a=BAJFJtWIB",
            "name": "Bitwarden",
            "description": "${description}",
            "email": "info@bitwarden.com",
            "telephone": "+1-904-664-9194",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "1 N. Calle Cesar Chavez",
              "addressLocality": "Santa Barbara",
              "addressCountry": "USA",
              "addressRegion": "CA",
              "postalCode": "93103"
            }
          }`}
        </script>
      )}
    </Helmet>
  )
}
